import { Building2, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useInsuranceCarriers } from '@/models/insuranceCarrier'

interface CarrierPickerProps {
  /** Currently assigned carrier id, or null when none is assigned. */
  value: string | null
  onChange: (carrierId: string | null) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  /** Put on the trigger, so a <Label htmlFor> can name this picker. */
  id?: string
  /** Carrier ids to leave out, e.g. the one already being merged away. */
  exclude?: string[]
  /** Offer "No carrier"; off where clearing means nothing, like picking a merge source. */
  allowClear?: boolean
}

const NO_CARRIER = '__none__'

/**
 * Single-select for assigning an InsuranceCarrier record to an offer or policy.
 * Clearing it falls the row back to its vendor branding.
 */
export function CarrierPicker({
  value,
  onChange,
  placeholder = 'No carrier — use vendor branding',
  className,
  disabled,
  id,
  exclude,
  allowClear = true,
}: CarrierPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { carriers, isLoading } = useInsuranceCarriers()

  const selected = carriers.find((c) => c.id === value) ?? null

  const q = query.trim().toLowerCase()
  const offered = exclude?.length ? carriers.filter((c) => !exclude.includes(c.id)) : carriers
  const filtered = q ? offered.filter((c) => c.name.toLowerCase().includes(q)) : offered

  function select(carrierId: string | null) {
    onChange(carrierId)
    setOpen(false)
    setQuery('')
  }

  return (
    // `modal` so the list wheel-scrolls when opened inside a Dialog (matches CarrierSelect).
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled || isLoading}
          className={cn('h-10 w-full justify-start gap-2 font-normal', className)}
        >
          <Building2 className="text-muted-foreground size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">
            {selected ? (
              <span className="flex items-center gap-2">
                {selected.logo && (
                  <img src={selected.logo} alt="" className="size-4 rounded object-contain" />
                )}
                {selected.name}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search carriers…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandGroup>
              {allowClear && (
                <CommandItem value={NO_CARRIER} onSelect={() => select(null)} className="cursor-pointer">
                  <span className="text-muted-foreground">No carrier — use vendor branding</span>
                </CommandItem>
              )}
              {filtered.map((carrier) => (
                <CommandItem
                  key={carrier.id}
                  value={carrier.id}
                  onSelect={() => select(carrier.id)}
                  className="cursor-pointer"
                >
                  {carrier.logo && (
                    <img src={carrier.logo} alt="" className="size-4 rounded object-contain" />
                  )}
                  {carrier.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {filtered.length === 0 && (
              <div className="text-muted-foreground py-6 text-center text-sm">No carriers found</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
